import type { PaymentsConfigResponse, PropertyPublic } from "@townsy/contracts";
import { formatCents } from "../lib/format";

/**
 * Whether a dollar button (card + Townsy Credit) is usable right now, and the exact explanation
 * when it is not (CLAUDE.md: every shipped button works or is explicitly disabled with a reason).
 * These are presentation rules only: the server re-checks everything at quote, order and
 * fulfilment time.
 */

export type DollarBlockCode =
  | "PAYMENTS_LOADING"
  | "PAYMENTS_UNAVAILABLE"
  | "PAYMENTS_DISABLED"
  | "PROVIDER_UNSUPPORTED"
  | "SIGN_IN_REQUIRED"
  | "EMAIL_NOT_VERIFIED"
  | "TAKEOVERS_DISABLED";

export interface DollarGate {
  enabled: boolean;
  code: DollarBlockCode | null;
  reason: string | null;
}

export interface DollarViewer {
  isGuest: boolean;
  emailVerified: boolean;
}

export type ConfigState = "loading" | "ready" | "error";

// Founder 2026-10-27: townsy.io opens to the public before live payments ("Open now, buying off"), so this is what visitors read.
export const PAYMENTS_OFF_REASON = "Buying opens soon. Card and Townsy Credit purchases are not open yet.";

const OK: DollarGate = { enabled: true, code: null, reason: null };
const off = (code: DollarBlockCode, reason: string): DollarGate => ({ enabled: false, code, reason });

/**
 * True when Stripe.js can collect a card shortfall with this configuration: the publishable key must belong to the
 * mode the SERVER reports (ADR-0046: pk_live_ only in live mode, pk_test_ only in test mode; the browser never picks).
 */
export function canCollectCards(config: Pick<PaymentsConfigResponse, "provider" | "publishableKey"> & { mode?: PaymentsConfigResponse["mode"] }): boolean {
  const prefix = config.mode === "live" ? "pk_live_" : "pk_test_";
  return config.provider === "stripe" && typeof config.publishableKey === "string" && config.publishableKey.startsWith(prefix);
}

/**
 * The card step can run: Stripe (test or live mode, as the server says), or the API's simulated provider (local
 * development only; the server refuses it elsewhere), whose step is clearly labelled and has no card form.
 */
export function paymentStepAvailable(config: Pick<PaymentsConfigResponse, "provider" | "publishableKey"> & { mode?: PaymentsConfigResponse["mode"] }): boolean {
  return canCollectCards(config) || config.provider === "simulated";
}

/** ADR-0046: the short "who pays what" phrase next to dollar prices ("(Stripe test mode)" only in test mode). */
export function cardPhrase(mode: PaymentsConfigResponse["mode"] | null | undefined): string {
  return mode === "live" ? "a card" : "a card (Stripe test mode)";
}

export function dollarGate(input: {
  config: PaymentsConfigResponse | null;
  configState: ConfigState;
  viewer: DollarViewer;
  /** Commercial buildings also need `commercialTakeoversEnabled`. */
  commercial?: boolean;
}): DollarGate {
  const { config, configState, viewer } = input;
  if (configState === "loading" && !config) return off("PAYMENTS_LOADING", "Checking whether card payments are available…");
  if (!config) return off("PAYMENTS_UNAVAILABLE", "Card payments could not be checked right now. Reload the page to try again.");
  if (!config.enabled || config.provider === "disabled") return off("PAYMENTS_DISABLED", PAYMENTS_OFF_REASON);
  if (input.commercial && !config.commercialTakeoversEnabled) {
    return off("TAKEOVERS_DISABLED", "Buying and taking over stores is switched off in this environment.");
  }
  if (!paymentStepAvailable(config)) {
    return off("PROVIDER_UNSUPPORTED", config.mode === "live" ? "Card payments are not configured on this server." : "Card payments are not configured for test mode on this server.");
  }
  if (viewer.isGuest) return off("SIGN_IN_REQUIRED", "Sign in to pay with a card or Townsy Credit. Guests cannot buy.");
  if (!viewer.emailVerified) return off("EMAIL_NOT_VERIFIED", "Verify your email address to pay with a card or Townsy Credit.");
  return OK;
}

const PAYMENTS_LEVEL: ReadonlySet<DollarBlockCode> = new Set(["PAYMENTS_LOADING", "PAYMENTS_UNAVAILABLE", "PAYMENTS_DISABLED", "PROVIDER_UNSUPPORTED", "TAKEOVERS_DISABLED"]);

/**
 * Combine the payments gate with a purchase-specific rule (plot limit, no home yet, top level...).
 * "Payments are off" wins (it is the most basic reason); then the purchase rule; then sign-in/email.
 */
export function withDomainBlock(gate: DollarGate, domain: { reason: string | null } | null): DollarGate {
  if (!gate.enabled && gate.code && PAYMENTS_LEVEL.has(gate.code)) return gate;
  if (domain?.reason) return { enabled: false, code: gate.code, reason: domain.reason };
  return gate;
}

/** Residential plot in dollars: the listing's own block (limit, sign-in, feature) applies too. */
export function plotDollarAvailability(
  listings: { blockedReason: string | null; plotsPerAccount: number; ownedPropertyIds: readonly string[] },
  gate: DollarGate,
): DollarGate {
  let reason: string | null = null;
  switch (listings.blockedReason) {
    case "SIGN_IN_REQUIRED":
      reason = "Sign in to buy a plot. Guests can look around but cannot own homes.";
      break;
    case "EMAIL_NOT_VERIFIED":
      reason = "Verify your email address to buy a plot.";
      break;
    case "PLOT_LIMIT_REACHED":
      reason =
        listings.plotsPerAccount === 1
          ? "One plot per account: you already own a plot (or one is held for your checkout)."
          : `You already own ${listings.ownedPropertyIds.length} plots (limit ${listings.plotsPerAccount} per account).`;
      break;
    case "FEATURE_DISABLED":
      reason = "Plot sales are switched off in this environment.";
      break;
    default:
      break;
  }
  return withDomainBlock(gate, { reason });
}

// ---------------------------------------------------------------- commercial buy / take over

export interface CommercialOfferInput {
  property: Pick<
    PropertyPublic,
    "id" | "type" | "status" | "address" | "version" | "ownerLabel" | "ownerActorId" | "ownershipIntervalId" | "currentBasisCents" | "nextPriceCents" | "placement"
  >;
  gate: DollarGate;
  /** The viewer's public actor id (bootstrap). */
  viewerActorId: string | null;
  /** From `GET /properties/:id/my-permissions` (null while unknown / guests). */
  isOwner: boolean | null;
  /** A recent PROPERTY_RESERVED answer for this property (another buyer's checkout holds it). */
  reservedByOther?: boolean;
  /** ADR-0046: the server's Stripe mode (GET /payments/config); default test wording. */
  paymentsMode?: PaymentsConfigResponse["mode"] | null;
}

export interface CommercialOffer {
  mode: "buy" | "takeover";
  /** What the buyer pays now (the server's next price), or null when unpriced. */
  priceCents: number | null;
  /** Button label: "Take over for $12.00" / "Buy for $10.00". */
  label: string;
  enabled: boolean;
  reason: string | null;
  isOwner: boolean;
  /** Townsy-held demo store: no reimbursable basis, nobody receives credit. */
  isDemo: boolean;
  /** Credit the current owner receives when the purchase completes (null: none). */
  ownerCreditCents: number | null;
  /** Plain-language explanation lines shown with the button. */
  lines: string[];
}

export const SELF_TAKEOVER_REASON = "You own this building. You cannot buy your own building: it would only raise its price.";

/** docs/01 section 6 "Purchase review": the required concept, verbatim in spirit. */
export const TAKEOVER_CONCEPT =
  "Other members can buy this commercial building at a higher price. If that happens, your eligible purchase amount returns as Townsy Credit, not a cash withdrawal.";

export function isDemoStore(p: Pick<PropertyPublic, "placement" | "ownershipIntervalId" | "currentBasisCents">): boolean {
  return Boolean(p.placement?.isDemo) || (p.ownershipIntervalId !== null && p.currentBasisCents === null);
}

export function commercialOffer(input: CommercialOfferInput): CommercialOffer {
  const p = input.property;
  const isDemo = isDemoStore(p);
  const unowned = p.ownershipIntervalId === null || p.status === "AVAILABLE";
  const mode: CommercialOffer["mode"] = unowned || isDemo ? "buy" : "takeover";
  const price = p.nextPriceCents;
  const priceText = formatCents(price);
  const label = price === null ? (mode === "buy" ? "Buy" : "Take over") : `${mode === "buy" ? "Buy" : "Take over"} for ${priceText}`;
  const isOwner = input.isOwner === true || (input.viewerActorId !== null && p.ownerActorId !== null && p.ownerActorId === input.viewerActorId);
  const ownerCreditCents = mode === "takeover" && !isDemo ? p.currentBasisCents : null;

  const lines: string[] = [];
  if (isOwner) {
    // The "you cannot buy your own building" reason is shown once, as the panel's note.
    lines.push(
      p.currentBasisCents !== null
        ? `If another member takes it over, you receive ${formatCents(p.currentBasisCents)} as Townsy Credit (100% of what you paid; not cash).`
        : "If another member takes it over, you receive what you paid as Townsy Credit (not cash).",
    );
  } else {
    if (isDemo) {
      lines.push("Demo store held by Townsy. Buying it gives nobody Townsy Credit: there is no previous purchase to reimburse.");
    } else if (unowned) {
      lines.push("Nobody owns this building yet.");
    } else if (ownerCreditCents !== null) {
      lines.push(
        `${p.ownerLabel ?? "The current owner"} receives ${formatCents(ownerCreditCents)} as Townsy Credit (100% of what they paid; not cash) when your takeover completes.`,
      );
    }
    if (price !== null) {
      lines.push(
        `You pay ${priceText}. That becomes your basis: if another member takes the building over later, you receive ${priceText} back as Townsy Credit, not cash.`,
      );
    }
    lines.push(
      input.paymentsMode === "live"
        ? "Your Townsy Credit is applied first; a card pays the rest and any sales tax."
        : "Your Townsy Credit is applied first; a card (Stripe test mode) pays the rest.",
    );
  }

  let enabled = input.gate.enabled;
  let reason = input.gate.reason;
  if (isOwner) {
    enabled = false;
    reason = SELF_TAKEOVER_REASON;
  } else if (p.type !== "COMMERCIAL") {
    enabled = false;
    reason = "Only commercial buildings can be bought or taken over. Homes can never be taken over.";
  } else if (p.status === "SUSPENDED") {
    enabled = false;
    reason = "This building is suspended and cannot be bought right now.";
  } else if (price === null) {
    enabled = false;
    reason = "This building has no price yet.";
  } else if (enabled && input.reservedByOther) {
    // Still clickable: the hold may have ended; the server answers again.
    reason = "Another member was checking out this building a moment ago. If their hold is still active you'll be told; nothing is charged.";
  }
  return { mode, priceCents: price, label, enabled, reason, isOwner, isDemo, ownerCreditCents, lines };
}
